import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, Bell, BookOpen, Building2, Home, LifeBuoy, Menu, MessageCircleHeart, Sparkles } from "lucide-react";
import { BrandLockup } from "@/components/brand";
import { anonymousStatus } from "@/lib/publicApi";
import { enablePush, listNotifications, onNativeToken, onNotificationOpen, pushStatus } from "@/lib/notificationsApi";
import { isNativeApp } from "@/lib/nativeBridge";

export const Skeleton = ({ lines = 3, testId = "skeleton-loading" }) => (
  <div data-testid={testId} className="space-y-3" aria-busy="true" aria-live="polite">
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="h-20 animate-pulse rounded-2xl bg-sand/80" />
    ))}
  </div>
);

export const EmergencyBar = () => (
  <Link
    to="/saude/ajuda-agora"
    data-testid="emergency-quick-access"
    className="flex items-center gap-3 rounded-2xl border border-danger/30 bg-danger/8 px-4 py-3 text-sm text-ink-700 transition-colors hover:bg-danger/12"
  >
    <LifeBuoy size={18} className="shrink-0 text-danger" />
    <span>
      <strong className="font-medium">Preciso de ajuda agora</strong>
      <span className="block text-xs text-ink-500">Este app não atende emergências. Veja os canais indicados.</span>
    </span>
  </Link>
);

export default function SaudeLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [unread, setUnread] = useState(0);
  const [pushUnread, setPushUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    anonymousStatus().then((data) => {
      if (alive) setUnread(data?.unread_count || 0);
    });
    listNotifications("saude")
      .then((data) => {
        if (alive) setPushUnread(data?.unread || 0);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [location.pathname]);

  // Permissão pedida nativamente na primeira abertura; aqui apenas registramos o token recebido.
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const offToken = onNativeToken("saude");
    const offOpen = onNotificationOpen((route) => navigate(route));
    (async () => {
      const status = await pushStatus();
      if (status?.granted) await enablePush("saude").catch(() => {});
    })();
    return () => {
      offToken();
      offOpen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const tabs = [
    { to: "/saude", label: "Início", icon: Home },
    { to: "/saude/tratamentos", label: "Tratamentos", icon: Sparkles },
    { to: "/saude/estrutura", label: "Estrutura", icon: Building2 },
    { to: "/saude/conteudos", label: "Conteúdos", icon: BookOpen },
    { to: "/saude/fale-com-a-erimus", label: "Fale com a Erimus", icon: MessageCircleHeart },
  ];

  const menu = [
    { to: "/saude/notificacoes", label: "Notificações" },
    { to: "/saude/salvos", label: "Conteúdos salvos" },
    { to: "/saude/recentes", label: "Vistos recentemente" },
    { to: "/saude/pagina/sobre-a-erimus", label: "Sobre a Erimus" },
    { to: "/saude/pagina/como-funciona-a-avaliacao", label: "Como funciona a avaliação" },
    { to: "/saude/pagina/privacidade", label: "Privacidade" },
    { to: "/saude/pagina/termos-de-uso", label: "Termos de uso" },
    { to: "/saude/ajuda-agora", label: "Aviso sobre emergências" },
    { to: "/saude/configuracoes", label: "Configurações" },
  ];

  return (
    <div data-testid="saude-layout" className="min-h-screen bg-bone">
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-md items-center justify-between px-5 py-4">
          <Link to="/saude" className="flex items-center gap-3">
            <BrandLockup size="sm" product="Saúde Mental" testId="saude-brand" />
          </Link>
          <div className="flex items-center gap-1">
          <Link
            to="/saude/notificacoes"
            data-testid="saude-notifications-bell"
            aria-label={pushUnread ? `Notificações — ${pushUnread} não lidas` : "Notificações"}
            className="relative rounded-full p-2 text-ink-600 transition-colors hover:bg-sand"
          >
            <Bell size={20} />
            {pushUnread > 0 ? (
              <span
                data-testid="saude-notifications-badge"
                className="absolute right-1 top-1 grid h-[17px] min-w-[17px] place-items-center rounded-full bg-sage-600 px-1 text-[10px] font-bold text-white ring-2 ring-white"
              >
                {pushUnread}
              </span>
            ) : null}
          </Link>
          <button
            data-testid="saude-menu-toggle"
            aria-label="Abrir menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-full p-2 text-ink-600 transition-colors hover:bg-sand"
          >
            <Menu size={20} />
          </button>
          </div>
        </div>

        {menuOpen ? (
          <div data-testid="saude-menu" className="mx-auto max-w-md border-t border-border px-5 py-3">
            {menu.map((item) => (
              <button
                key={item.to}
                data-testid={`saude-menu-${item.to.split("/").pop()}`}
                onClick={() => {
                  setMenuOpen(false);
                  navigate(item.to);
                }}
                className="block w-full rounded-xl px-3 py-3 text-left text-sm text-ink-600 transition-colors hover:bg-sand"
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      {offline ? (
        <p data-testid="offline-banner" className="mx-auto max-w-md px-5 pt-4">
          <span className="flex items-center gap-2 rounded-xl bg-warning/15 px-4 py-2 text-xs text-ink-700">
            <AlertTriangle size={14} /> Sem conexão. Mostrando conteúdos já baixados.
          </span>
        </p>
      ) : null}

      <main className="mx-auto max-w-md px-5 pb-36 pt-4">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto flex max-w-md items-stretch justify-between px-3 pb-3 pt-2">
          {tabs.map((tab) => {
            const active = tab.to === "/saude" ? location.pathname === "/saude" : location.pathname.startsWith(tab.to);
            return (
              <Link
                key={tab.to}
                to={tab.to}
                aria-current={active ? "page" : undefined}
                data-testid={`saude-tab-${tab.to === "/saude" ? "inicio" : (tab.to.split("/").pop() || "inicio")}`}
                className={`flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl px-1 py-1 text-[11px] leading-tight transition-colors ${
                  active ? "bg-sage-50 text-sage-700" : "text-ink-400 hover:text-sage-600"
                }`}
              >
                <span className="relative">
                  <tab.icon size={20} />
                  {unread > 0 && tab.to === "/saude/fale-com-a-erimus" ? (
                    <span
                      data-testid="tab-unread-dot"
                      aria-label="Resposta disponível"
                      className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-sage-600 ring-2 ring-white"
                    />
                  ) : null}
                </span>
                <span className="text-center">{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
